/**
 * @description 手动注入mobxStore数据；
 *              Qrn工程没有统一的入口文件，所以不能使用mobx-react的Provider和inject组合来统一注入store
 *              所以只能对每个继承QVie的文件单独使用Provider注入数据
 * @providesModule mobxInjectX
 */
import React from 'react';
import { Provider } from 'mobx-react';
import { isValidArray } from './modelUtils';
import { createClass } from '../common/createClass';
import { isFunction, isValidObject } from '../common/utils';

/**
 * 获取要注入store
 * @param rootStore
 * @param storeName
 * @return {rootStore}
 */
function getComponentStore(rootStore, storeName) {
    // 如果没有传storeName或者storeName为空（'', null, undefined, []），则返回整个store
    if (!isValidArray(storeName)) {
        return rootStore;
    }

    try {
        const stores = {};
        storeName.forEach(name => {
            if (name in rootStore) {
                stores[name] = rootStore[name];
            } else {
                console.warn(`store [${name}] not exist in rootStore. Please check arguments`);
            }
        });

        return stores;
    } catch (e) {
        reportScriptError(e);
        return rootStore;
    }
}

// 实例化的根store
let rootStore = {};

// 需要注册到store的react声明周期
const REACT_LIFE_CYCLE = [
    'componentDidMount', 'componentDidMount',
    'componentDidUpdate', 'componentWillUnmount'
];

/**
 * 为组件注入store
 * 如果没有提供storeName参数，则默认注入所有store
 * 如果提供了storeName参数，只注入storeName中的store
 * @param {React.Node} Component 要被注入store的组件
 * @param {Array<string>} storeName 要注入的store名称
 * @param {Boolean} isEntryPage 是否是项目入口页
 * @return {Function}
 */
export function QProvider(Component, storeName = [], isEntryPage = false) {
    return function (Container) {
        const name = Container.name;
        let _ref = null;

        /**
         * 校验并调用方法
         * @param method
         * @param arg
         */
        function invoke(method, arg) {
            // 获取QView声明周期
            const { bindEvents: evt } = _ref || {};

            evt && isFunction(evt[method]) && evt[method].call(_ref, arg);
            isFunction(rootStore[method]) && rootStore[method].call(rootStore, arg);
        }

        // 添加render方法、QView声明周期及安卓物理返回键
        const commonProps = [
                {
                    key: 'render',
                    value: function render() {
                        // 获取本组件需要注入的store
                        const { setRef } = this.props;

                        return (
                            <Provider {...this.stores}>
                                <Component {...this.props} setRef={c => {
                                    _ref = c;
                                    isFunction(setRef) && setRef(c);
                                }} />
                            </Provider>
                        );
                    }
                },
                {
                    key: 'stores',
                    get: function () {
                        const { _mobxStore_ } = this;
                        // 设置rootStore
                        if (isValidObject(_mobxStore_)) {
                            rootStore = _mobxStore_;
                        }

                        return getComponentStore(rootStore, storeName);
                    }
                },
                // 响应QView使用的生命周期时间
                {
                    key: 'bindEvents',
                    value: {
                        ready() {
                            invoke('ready');
                            console.log(`[${name}] ready`);
                        },
                        actived(param) {
                            invoke('actived', param);
                            console.log(`[${name}] actived`);
                        },
                        deactived() {
                            invoke('deactived');
                            console.log(`[${name}] deactived`);
                        },
                        destroy() {
                            invoke('destroy');
                            console.log(`[${name}] destroy`);
                        }
                    }
                },
                // 安卓物理返回键
                {
                    key: 'onBackPressed',
                    value: function onBackPressed(...args) {
                        return _ref && isFunction(_ref.onBackPressed) && _ref.onBackPressed(...args);
                    }
                }
            ],
            lifeCycleProps = REACT_LIFE_CYCLE.map(name => ({
                key: name,
                value: function (...args) {
                    isFunction(Container[name]) && Container[name](...args);
                    isFunction(rootStore[name]) && rootStore[name](...args);
                }
            })),
            // 注册react声明周期（只有入口页需要添加）
            props = isEntryPage ? [...commonProps, ...lifeCycleProps] : commonProps;

        createClass(Container, props);
    };
}
